import { FC, useMemo } from 'react';
import { AnalyticsUtils } from '@frontend/analytics-shared';
import { BarChartAppearance } from '@frontend/charts';
import { useIsSmallMode } from '@frontend/shared';
import { usePhoneAnalyticsShallowStore } from '../hooks/use-phone-analytics-store';
import { AverageCallDurationChart } from './charts/average-call-duration-chart';
import { AverageIncomingCallDurationChart } from './charts/average-incoming-call-duration-chart';
import { LongDurationCallsChart } from './charts/long-duration-calls-chart';
import { PatientsVsUnknownCallDurationChart } from './charts/patients-vs-unknown-call-duration-chart';
import { ChartProps } from './charts/shared-types';
import { TotalCallDurationChart } from './charts/total-call-duration-chart';
import { SmallModePanelSummary } from './small-mode-panel-summary';

interface Props {
  defaultChartAppearance?: Partial<BarChartAppearance>;
}

export const DurationAnalyticsPanel: FC<React.PropsWithChildren<Props>> = ({ defaultChartAppearance = {} }) => {
  const { isSmallMode } = useIsSmallMode();
  const { accessibleLocationsNames, filters } = usePhoneAnalyticsShallowStore('accessibleLocationsNames', 'filters');

  const exportPdfProps = useMemo(
    () => AnalyticsUtils.exportChartToPdfProps(filters.location_id || [], accessibleLocationsNames, !isSmallMode),
    [isSmallMode, filters.location_id, accessibleLocationsNames]
  );

  const commonProps: ChartProps = {
    defaultChartAppearance: {
      ...defaultChartAppearance,
      customYAxisTick: undefined,
      customYAxisTickFormat: AnalyticsUtils.toHHmmss,
    },
    exportPdfProps,
    isSmallMode,
  };

  return (
    <>
      {isSmallMode && <SmallModePanelSummary type='callDuration' />}
      <TotalCallDurationChart {...commonProps} />
      <PatientsVsUnknownCallDurationChart {...commonProps} />
      <AverageCallDurationChart {...commonProps} />
      <AverageIncomingCallDurationChart {...commonProps} />
      <LongDurationCallsChart {...commonProps} />
    </>
  );
};
